import React, { useState, useEffect, useCallback } from 'react';
import {
  getPairsByRound,
  activatePair,
  finishPair,
  disqualifyPair,
  generatePairsForRound
} from '../../api/battlePairs';
import { useBattlePairsWebSocket, formatPairEventMessage } from '../../hooks/useBattlePairsWebSocket';
import { PairEventType } from '../../api/battlePairsWebSocket';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbCard from '../controls/HrnbCard';
import HrnbModal from '../controls/HrnbModal';
import '../../css/components/BattlePairsList.css';

/**
 * Список пар участников баттла с WebSocket обновлениями
 * 
 * @component
 * @param {Object} props
 * @param {string} props.roundId - UUID раунда
 * @param {boolean} [props.isAdmin=false] - Права администратора/модератора
 * @param {Function} [props.onPairSelect] - Обработчик выбора пары
 */
const BattlePairsList = ({ roundId, isAdmin = false, onPairSelect }) => {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // WebSocket подписка
  const {
    connected,
    error: wsError
  } = useBattlePairsWebSocket({
    roundId,
    onEvent: handleWebSocketEvent,
    autoConnect: true
  });

  /**
   * Обработка WebSocket событий
   */
  function handleWebSocketEvent(event) {
    console.log('[BattlePairsList] WebSocket event:', event);

    // Показываем уведомление
    showNotification(formatPairEventMessage(event), 'info');

    // Обновляем список пар в зависимости от типа события
    switch (event.eventType) {
      case PairEventType.DRAW_COMPLETED:
        // Перезагружаем список после жеребьёвки
        loadPairs();
        break;

      case PairEventType.PAIR_CREATED:
      case PairEventType.PAIR_UPDATED:
      case PairEventType.PAIR_ACTIVATED:
      case PairEventType.PAIR_FINISHED:
      case PairEventType.PAIR_DISQUALIFIED:
      case PairEventType.WINNER_ANNOUNCED:
        // Обновляем конкретную пару или перезагружаем список
        loadPairs();
        break;

      case PairEventType.PAIR_DELETED:
        // Удаляем пару из списка
        setPairs((prev) => prev.filter((p) => p.pairNumber !== event.pairNumber));
        break;

      case PairEventType.TRACK_UPLOADED:
        // Можно обновить счётчик треков в паре
        loadPairs();
        break;

      default:
        console.warn('[BattlePairsList] Unknown event type:', event.eventType);
    }
  }

  /**
   * Загрузка списка пар
   */
  const loadPairs = useCallback(async () => {
    if (!roundId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getPairsByRound({ roundId, size: 100 });
      setPairs(response.data.content || response.data);
    } catch (err) {
      console.error('[BattlePairsList] Error loading pairs:', err);
      setError('Не удалось загрузить пары участников');
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  useEffect(() => {
    loadPairs();
  }, [loadPairs]);

  /**
   * Показать уведомление
   */
  function showNotification(message, type = 'info') {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }

  /**
   * Генерация пар (жеребьёвка)
   */
  const handleGeneratePairs = async (drawType = 'RANDOM') => {
    try {
      setGenerating(true);
      await generatePairsForRound(roundId, { drawType, shuffle: true });
      showNotification('Жеребьёвка успешно проведена', 'success');
      setShowGenerateModal(false);
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error generating pairs:', err);
      showNotification('Ошибка при проведении жеребьёвки', 'danger');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Активация пары
   */
  const handleActivatePair = async (pairId) => {
    try {
      setActionInProgress(pairId);
      await activatePair(pairId);
      showNotification('Пара активирована', 'success');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error activating pair:', err);
      showNotification('Ошибка при активации пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Завершение пары с объявлением победителя
   */
  const handleFinishPair = async (pairId, winnerId) => {
    try {
      setActionInProgress(pairId);
      await finishPair(pairId, winnerId);
      showNotification('Пара завершена', 'success');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error finishing pair:', err);
      showNotification('Ошибка при завершении пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Дисквалификация пары
   */
  const handleDisqualifyPair = async (pairId) => {
    try {
      setActionInProgress(pairId);
      await disqualifyPair(pairId);
      showNotification('Пара дисквалифицирована', 'warning');
      loadPairs();
    } catch (err) {
      console.error('[BattlePairsList] Error disqualifying pair:', err);
      showNotification('Ошибка при дисквалификации пары', 'danger');
    } finally {
      setActionInProgress(null);
    }
  };

  /**
   * Получить класс статуса пары
   */
  function getPairStatusClass(status) {
    const statusClasses = {
      PENDING: 'pair-status-pending',
      ACTIVE: 'pair-status-active',
      FINISHED: 'pair-status-finished',
      DISQUALIFIED: 'pair-status-disqualified'
    };
    return statusClasses[status] || '';
  }

  /**
   * Получить отображение статуса
   */
  function getStatusLabel(status) {
    const labels = {
      PENDING: 'Ожидание',
      ACTIVE: 'Идёт голосование',
      FINISHED: 'Завершена',
      DISQUALIFIED: 'Дисквалифицирована'
    };
    return labels[status] || status;
  }

  if (loading) {
    return <HrnbLoader text="Загрузка пар участников..." />;
  }

  if (error) {
    return <HrnbAlert variant="danger">{error}</HrnbAlert>;
  }

  return (
    <div className="battle-pairs-list">
      {/* Уведомления */}
      {notification && (
        <HrnbAlert variant={notification.variant || 'info'} onClose={() => setNotification(null)}>
          {notification.message}
        </HrnbAlert>
      )}

      {/* WebSocket статус */}
      <div className="battle-pairs__status">
        <span className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}>
          {connected ? '●' : '○'} {connected ? 'Live-обновления' : 'Нет соединения'}
        </span>
        {wsError && <span className="error-text">Ошибка: {wsError.message}</span>}
      </div>

      {/* Кнопка генерации для админов */}
      {isAdmin && pairs.length === 0 && (
        <div className="battle-pairs__actions">
          <HrnbButton
            variant="primary"
            onClick={() => setShowGenerateModal(true)}
            disabled={generating}
          >
            {generating ? 'Жеребьёвка...' : 'Провести жеребьёвку'}
          </HrnbButton>
        </div>
      )}

      {/* Список пар */}
      {pairs.length === 0 ? (
        <HrnbAlert variant="info">
          {isAdmin
            ? 'Пары ещё не сформированы. Проведите жеребьёвку.'
            : 'Пары участников ещё не сформированы'}
        </HrnbAlert>
      ) : (
        <div className="pairs-grid">
          {pairs.map((pair) => (
            <HrnbCard
              key={pair.id}
              className={`pair-card ${getPairStatusClass(pair.status)}`}
            >
              <div className="pair-card__header">
                <span className="pair-number">Пара #{pair.pairNumber}</span>
                <span className={`pair-status ${getPairStatusClass(pair.status)}`}>
                  {getStatusLabel(pair.status)}
                </span>
              </div>

              <div className="pair-card__participants">
                <div className="participant first">
                  <span className="participant-name">
                    {pair.firstPerson?.displayName || 'Участник 1'}
                  </span>
                </div>
                <div className="vs">VS</div>
                <div className="participant second">
                  <span className="participant-name">
                    {pair.secondPerson?.displayName || 'Участник 2'}
                  </span>
                </div>
              </div>

              {pair.winner && (
                <div className="pair-card__winner">
                  🏆 Победитель: {pair.winner.displayName}
                </div>
              )}

              {/* Действия для админов */}
              {isAdmin && (
                <div className="pair-card__actions">
                  {pair.status === 'PENDING' && (
                    <HrnbButton
                      size="sm"
                      variant="success"
                      onClick={() => handleActivatePair(pair.id)}
                      disabled={actionInProgress === pair.id}
                    >
                      Активировать
                    </HrnbButton>
                  )}

                  {pair.status === 'ACTIVE' && (
                    <>
                      <HrnbButton
                        size="sm"
                        variant="primary"
                        onClick={() =>
                          handleFinishPair(pair.id, pair.firstPerson.id)
                        }
                        disabled={actionInProgress === pair.id}
                      >
                        Победа 1
                      </HrnbButton>
                      <HrnbButton
                        size="sm"
                        variant="primary"
                        onClick={() =>
                          handleFinishPair(pair.id, pair.secondPerson.id)
                        }
                        disabled={actionInProgress === pair.id}
                      >
                        Победа 2
                      </HrnbButton>
                      <HrnbButton
                        size="sm"
                        variant="danger"
                        onClick={() => handleDisqualifyPair(pair.id)}
                        disabled={actionInProgress === pair.id}
                      >
                        Дисквалифицировать
                      </HrnbButton>
                    </>
                  )}
                </div>
              )}
            </HrnbCard>
          ))}
        </div>
      )}

      {/* Модальное окно жеребьёвки */}
      <HrnbModal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
        title="Проведение жеребьёвки"
      >
        <div className="generate-modal">
          <p>Выберите тип жеребьёвки:</p>
          <div className="generate-modal__buttons">
            <HrnbButton
              variant="primary"
              onClick={() => handleGeneratePairs('RANDOM')}
              disabled={generating}
            >
              Случайная
            </HrnbButton>
            <HrnbButton
              variant="secondary"
              onClick={() => handleGeneratePairs('SEEDED')}
              disabled={generating}
            >
              По рейтингу
            </HrnbButton>
          </div>
        </div>
      </HrnbModal>
    </div>
  );
};

export default BattlePairsList;
